const ED25519 = { name: "Ed25519" };
const AES_GCM = { length: 256, name: "AES-GCM" };
const ED25519_PKCS8_PREFIX = Uint8Array.from([
    0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
    0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);
export async function generateNonExtractableKeyMaterial(cryptoProvider) {
    const pair = await cryptoProvider.subtle.generateKey(ED25519, false, ["sign", "verify"]);
    if (!isCryptoKeyPair(pair))
        throw new Error("Device identity generation failed");
    const publicKey = new Uint8Array(await cryptoProvider.subtle.exportKey("raw", pair.publicKey));
    const keyId = await deriveKeyId(cryptoProvider, publicKey);
    const keyMaterial = {
        createdAt: new Date().toISOString(),
        custodyMode: "non-extractable",
        keyId,
        privateKey: pair.privateKey,
        publicKey,
        version: 1,
    };
    return { keyMaterial, publicKey };
}
export async function generateWrappedKeyMaterial(cryptoProvider) {
    const pair = await cryptoProvider.subtle.generateKey(ED25519, true, ["sign", "verify"]);
    if (!isCryptoKeyPair(pair))
        throw new Error("Device identity generation failed");
    const wrappingKey = await cryptoProvider.subtle.generateKey(AES_GCM, false, ["wrapKey", "unwrapKey"]);
    if (isCryptoKeyPair(wrappingKey))
        throw new Error("Device identity generation failed");
    const iv = cryptoProvider.getRandomValues(new Uint8Array(12));
    const wrappedPrivateKey = new Uint8Array(await cryptoProvider.subtle.wrapKey("pkcs8", pair.privateKey, wrappingKey, { iv, name: "AES-GCM" }));
    const publicKey = new Uint8Array(await cryptoProvider.subtle.exportKey("raw", pair.publicKey));
    const keyId = await deriveKeyId(cryptoProvider, publicKey);
    const keyMaterial = {
        createdAt: new Date().toISOString(),
        custodyMode: "wrapped",
        iv,
        keyId,
        publicKey,
        version: 1,
        wrappedPrivateKey,
        wrappingKey,
    };
    return { keyMaterial, publicKey };
}
export async function signWithKeyMaterial(cryptoProvider, keyMaterial, payload) {
    const privateKey = keyMaterial.custodyMode === "non-extractable"
        ? keyMaterial.privateKey
        : await cryptoProvider.subtle.unwrapKey("pkcs8", arrayBuffer(keyMaterial.wrappedPrivateKey), keyMaterial.wrappingKey, { iv: arrayBuffer(keyMaterial.iv), name: "AES-GCM" }, ED25519, false, ["sign"]);
    return new Uint8Array(await cryptoProvider.subtle.sign(ED25519, privateKey, arrayBuffer(payload)));
}
export async function withWrappedPrivateKeySeedForIroh(cryptoProvider, keyMaterial, usePrivateKey) {
    const privateKey = await cryptoProvider.subtle.unwrapKey("pkcs8", arrayBuffer(keyMaterial.wrappedPrivateKey), keyMaterial.wrappingKey, { iv: arrayBuffer(keyMaterial.iv), name: "AES-GCM" }, ED25519, true, ["sign"]);
    const pkcs8 = new Uint8Array(await cryptoProvider.subtle.exportKey("pkcs8", privateKey));
    let seed;
    try {
        seed = extractEd25519Seed(pkcs8);
        return await usePrivateKey(seed);
    }
    finally {
        seed?.fill(0);
        pkcs8.fill(0);
    }
}
export async function inspectKeyMaterial(cryptoProvider, keyMaterial) {
    if (!isStoredKeyMaterial(keyMaterial))
        return "corrupt";
    if (await deriveKeyId(cryptoProvider, keyMaterial.publicKey) !== keyMaterial.keyId)
        return "corrupt";
    const payload = new TextEncoder().encode("node-client-key-check-v1");
    let signature;
    try {
        signature = await signWithKeyMaterial(cryptoProvider, keyMaterial, payload);
    }
    catch (error) {
        if (keyMaterial.custodyMode === "wrapped" && isWrappedKeyCorruption(error))
            return "corrupt";
        throw error;
    }
    const publicKey = await cryptoProvider.subtle.importKey("raw", arrayBuffer(keyMaterial.publicKey), ED25519, false, ["verify"]);
    return await cryptoProvider.subtle.verify(ED25519, publicKey, arrayBuffer(signature), arrayBuffer(payload))
        ? "valid"
        : "corrupt";
}
export function isStoredKeyMaterial(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const candidate = value;
    if (candidate.version !== 1 ||
        typeof candidate.keyId !== "string" || candidate.keyId.length === 0 ||
        !(candidate.publicKey instanceof Uint8Array) || candidate.publicKey.byteLength !== 32) {
        return false;
    }
    if (candidate.custodyMode === "non-extractable") {
        return isSigningKey(candidate.privateKey);
    }
    return (candidate.custodyMode === "wrapped" &&
        candidate.iv instanceof Uint8Array && candidate.iv.byteLength === 12 &&
        candidate.wrappedPrivateKey instanceof Uint8Array && candidate.wrappedPrivateKey.byteLength > 0 &&
        isWrappingKey(candidate.wrappingKey));
}
function isCryptoKeyPair(value) {
    return "privateKey" in value && "publicKey" in value;
}
function isSigningKey(value) {
    return isCryptoKey(value) &&
        value.algorithm.name === "Ed25519" &&
        value.extractable === false &&
        value.type === "private" &&
        value.usages.length === 1 && value.usages[0] === "sign";
}
function isWrappingKey(value) {
    if (!isCryptoKey(value) || value.algorithm.name !== "AES-GCM")
        return false;
    const algorithm = value.algorithm;
    return algorithm.length === 256 &&
        value.extractable === false &&
        value.type === "secret" &&
        value.usages.includes("unwrapKey") && value.usages.includes("wrapKey");
}
function isCryptoKey(value) {
    return typeof value === "object" && value !== null &&
        "algorithm" in value && "extractable" in value && "type" in value && "usages" in value;
}
function isWrappedKeyCorruption(error) {
    return error instanceof DOMException && ["DataError", "InvalidAccessError", "OperationError"]
        .includes(error.name);
}
function extractEd25519Seed(pkcs8) {
    if (pkcs8.byteLength !== ED25519_PKCS8_PREFIX.byteLength + 32 ||
        !ED25519_PKCS8_PREFIX.every((byte, index) => pkcs8[index] === byte)) {
        throw new Error("Unsupported Ed25519 PKCS8 encoding");
    }
    return pkcs8.slice(ED25519_PKCS8_PREFIX.byteLength);
}
async function deriveKeyId(cryptoProvider, publicKey) {
    const digest = new Uint8Array(await cryptoProvider.subtle.digest("SHA-256", arrayBuffer(publicKey)));
    return [...digest.slice(0, 16)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
function arrayBuffer(bytes) {
    return new Uint8Array(bytes).buffer;
}
//# sourceMappingURL=key-custody.js.map